import * as React from 'react';
import { useCallback, useMemo, useRef, useState } from 'react';
import { Button, ButtonGroup, Content, Dialog, Divider, Footer, Header, Heading, ProgressBar, Text, View } from '@adobe/react-spectrum';
import { useTranslation } from 'react-i18next';
import { FinderContextTree } from '../../../pages/protected/finder/components/FinderContextTree';
import { ProcessMoveResult, useFinderContextTree } from '../../../pages/protected/finder/components/FinderContextTree.hooks';
import Add from '@spectrum-icons/workflow/Add';
import { Key, Selection } from '@adobe/react-spectrum';
import { FPAData, FPADataTypes } from '../../../infra/protected/FPA/FPAData';
import { FPAItem } from '../../../infra/protected/FPA/FPAItem';
import { alignRevealPathIdsForFinderTree } from '../../../utils/finder/alignRevealPathIdsForFinderTree';
import { findItemInTree } from '../../../utils/finder/findItemInTree';
import { findPathToItem } from '../../../utils/finder/findPathToItem';
import { contextObjectFromFpaPath, minimalFpaItemFromContextObject } from '../../../utils/finder/contextObjectFromFpaPath';
import { showContextMoveSuccessToast } from './contextMoveSuccessEffects';
import { ContextSelection } from '../Search/ContextSelection';
import { contextObjectFromGetContextPathList } from '../Search/ContextSelection/contextPathToContextObject';
import type { ContextObject } from '../Search/ContextSelection/types';
import { useDependency } from '../../../contexts/DependencyProvider';
import { usePreloadAssets } from '../../../hooks/UsePreloadAssets';
import { GetContextPathRequest } from '../../../services/soap/project/requests/GetContextPathRequest';

/** i18n interpolation keys passed to finder namespace strings in this dialog. */
interface ContextMoveDialogTParams {
    item?: string;
    context?: FPADataTypes;
}

export interface IContextMoveDialogProps {
    onClose: (move: boolean) => void;
    parentItem: FPAData | null;
    /** When `onMoveContextSelected` is omitted, must be non-null for move. May be null when using `onMoveContextSelected`. */
    selectedItem: FPAData | null;
    showActivities: boolean;
    showProjects: boolean;
    showFolders: boolean;
    selectedItemType: FPADataTypes;
    relatedToActivityType?: number;
    fpaTypeCategories?: string;
    /**
     * When set, performs move via this callback with the user-selected destination tree node instead of `processMove`.
     * Caller owns toasts; the dialog does not show `showContextMoveSuccessToast` for this path.
     */
    onMoveContextSelected?: (selectedItem: FPAItem) => Promise<ProcessMoveResult>;
}

export function ContextMoveDialog ({ onClose, parentItem, selectedItem, showActivities, showProjects, showFolders, relatedToActivityType, fpaTypeCategories, selectedItemType, onMoveContextSelected }: IContextMoveDialogProps) {
    const { t } = useTranslation();
    const _t = useCallback(
        (key: string, params: ContextMoveDialogTParams = {}) =>
            t(`contextMoveDialog.${key}`, { ns: 'finder', ...params }) as string,
        [t]
    );

    const {
        folderStatusMapping,
        projectStatusMapping,
        activityStatusMapping,
    } = usePreloadAssets();
    const { store, projectService } = useDependency();

    const [destinationContext, setDestinationContext] = useState<ContextObject | null>(null);
    const [expandedKeys, setExpandedKeys] = React.useState<Set<Key>>(new Set());
    const [selectedKeys, setSelectedKeys] = React.useState<Selection>(new Set());
    const [scrollIntoViewKey, setScrollIntoViewKey] = useState<number | string | null>(null);
    const [movePending, setMovePending] = useState(false);
    const revealGenerationRef = useRef(0);

    const { treeData, onSelection, loading, processMove, revealPath } = useFinderContextTree({
        parentItem: parentItem,
        showFolders: showFolders,
        showProjects: showProjects,
        showActivities: showActivities,
        additionalFilter: {
            relatedToActivityType: relatedToActivityType,
            fpaTypeCategories: fpaTypeCategories
        }
    });

    const moveItemResolved = useMemo(() => {
        if (!destinationContext?.contextId) {
            return null;
        }
        const inTree = findItemInTree(treeData, destinationContext.contextId);
        if (inTree) {
            return inTree;
        }
        return minimalFpaItemFromContextObject(destinationContext);
    }, [treeData, destinationContext]);

    const allowMove = useMemo(() => {
        if (!moveItemResolved) {
            return false;
        }
        if (onMoveContextSelected) {
            return moveItemResolved.type !== FPADataTypes.FOLDER_TYPE;
        }
        return moveItemResolved.type === selectedItemType;
    }, [moveItemResolved, selectedItemType, onMoveContextSelected]);

    const onContextPathResolveError = useCallback((error: unknown) => {
        console.error('ContextMoveDialog context path resolve failed', error);
    }, []);

    const handleContextSelectionChange = useCallback(
        (ctx: ContextObject | null) => {
            setDestinationContext(ctx);
            if (!ctx) {
                revealGenerationRef.current += 1;
                setSelectedKeys(new Set());
                setScrollIntoViewKey(null);
                return;
            }
            void (async () => {
                revealGenerationRef.current += 1;
                const gen = revealGenerationRef.current;

                /** Full chain from API so reveal ids match folder-type roots (ListFPA search rows are often shallow). */
                let pathSource: ContextObject = ctx;
                try {
                    const res = await projectService.getContextPath(
                        new GetContextPathRequest(store.Server, store.SessionId, ctx.contextId)
                    );
                    if (res.LIST?.length && res.count > 0) {
                        const resolved = contextObjectFromGetContextPathList(res.LIST);
                        if (resolved?.pathSegmentContexts?.length) {
                            pathSource = resolved;
                        }
                    }
                } catch {
                    /* use ctx */
                }
                if (gen !== revealGenerationRef.current) {
                    return;
                }

                const pathIdsRaw =
                    pathSource.pathSegmentContexts?.length
                        ? pathSource.pathSegmentContexts.map((p) => p.id)
                        : findPathToItem(treeData, ctx.contextId)?.map((n) => n.id) ?? [];
                const ancestorIds = alignRevealPathIdsForFinderTree(
                    treeData,
                    pathIdsRaw,
                    pathSource.folderTypeId ?? ctx.folderTypeId
                );

                if (ancestorIds.length > 0) {
                    try {
                        await revealPath(ancestorIds);
                    } catch (e) {
                        console.error(e);
                    }
                }
                if (gen !== revealGenerationRef.current) {
                    return;
                }

                setExpandedKeys((prev) => {
                    const next = new Set(prev);
                    for (const id of ancestorIds) {
                        next.add(String(id));
                    }
                    return next;
                });
                setSelectedKeys(new Set([String(ctx.contextId)]));
                setScrollIntoViewKey(ctx.contextId);
            })();
        },
        [revealPath, treeData, projectService, store.Server, store.SessionId]
    );

    const handleTreeSelection = useCallback(
        (key: string) => {
            revealGenerationRef.current += 1;
            const path = findPathToItem(treeData, key);
            if (path?.length) {
                setDestinationContext(contextObjectFromFpaPath(path));
            } else {
                const item = findItemInTree(treeData, key);
                if (item) {
                    setDestinationContext(contextObjectFromFpaPath([item]));
                }
            }
            setScrollIntoViewKey(Number(key));
            void onSelection(Number(key));
        },
        [treeData, onSelection]
    );

    const handleMove = useCallback(async () => {
        if (!moveItemResolved) {
            return;
        }
        if (onMoveContextSelected) {
            setMovePending(true);
            const move_result: ProcessMoveResult = await onMoveContextSelected(moveItemResolved);
            if (move_result.moved) {
                onClose(true);
            } else {
                setMovePending(false);
            }
            return;
        }
        if (!selectedItem) {
            return;
        }
        setMovePending(true);
        const move_result: ProcessMoveResult = await processMove(selectedItem.type, '' + selectedItem.id, '' + moveItemResolved.id);
        if (move_result.moved) {
            onClose(true);
            showContextMoveSuccessToast(
                move_result.itemId,
                selectedItem.type,
                _t('toast.move_success', { item: moveItemResolved.title, context: moveItemResolved.type }),
                _t('toast.open_item_label', { item: moveItemResolved.title, context: moveItemResolved.type })
            );
        } else {
            setMovePending(false);
        }
    }, [moveItemResolved, selectedItem, processMove, onClose, _t, onMoveContextSelected]);

    const contextAriaLabel = t('choose_context', { ns: 'finder', defaultValue: 'Choose context' }) as string;

    return (
        <div>
            <Dialog>
                <Heading>{_t('move_context')}</Heading>
                <Header>
                    <Button variant='primary' onPress={() => onClose(false)}>
                        <Add />
                        <Text>{_t('add_new_context')}</Text>
                    </Button>
                </Header>
                <Divider />
                <Content>
                    <View marginBottom='size-150'>
                        <ContextSelection
                            value={destinationContext}
                            onSelectionChange={handleContextSelectionChange}
                            folderStatusMap={folderStatusMapping}
                            projectStatusMap={projectStatusMapping}
                            activityStatusMap={activityStatusMapping}
                            isReadOnly={false}
                            showBorders={true}
                            allowTransparentBG={false}
                            allowClearSelection={true}
                            allowAdd={false}
                            onExplicitContextResolveError={onContextPathResolveError}
                            aria-label={contextAriaLabel}
                        />
                    </View>
                    <FinderContextTree
                        items={treeData}
                        onSelection={handleTreeSelection}
                        expandedKeys={expandedKeys}
                        setExpandedKeys={setExpandedKeys}
                        selectedKeys={selectedKeys}
                        setSelectedKeys={setSelectedKeys}
                        scrollIntoViewKey={scrollIntoViewKey}
                    />
                </Content>
                <Footer>
                    <View>
                        {loading && (<ProgressBar label={_t('loading')+'...'} isIndeterminate />)}
                    </View>
                </Footer>
                <ButtonGroup>
                    <Button variant='secondary' onPress={() => onClose(false)}>{_t('cancel')}</Button>
                    <Button variant='accent' onPress={handleMove} isDisabled={!allowMove || movePending}>{_t('move')}</Button>
                </ButtonGroup>
            </Dialog>
        </div>
    );
}
